/**
 * The notifications bell's seen/read state (NBO-2207): the pure half.
 *
 * Which assigned actions the user has already seen (badge) and read (row brand-edge) is a
 * `notifications` preference on the account since NBO-2518 — TENANT-scoped, because tickets are
 * tenant-scoped — through NotificationStatePreference. This file keeps the value's rules (sanitising,
 * the cap, `withIds`).
 */

export interface NotificationSeenState {
  /** Ticket ids whose arrival the badge has already counted. Newest first. */
  seenIds: string[];
  /** Ticket ids the user has opened or marked read. Newest first. */
  readIds: string[];
}

/**
 * Caps each id set. 300 uuids ≈ 11.7 KB per set, ≈ 23 KB for the value — inside the 32 KB a
 * preference value may hold. Ids fall off the tail (oldest), which is safe: a ticket old enough to be
 * evicted has long left the 20-row poll window, so its unread state can never be asked for again.
 */
const MAX_IDS = 300;

export const EMPTY_NOTIFICATION_STATE: NotificationSeenState = { seenIds: [], readIds: [] };

/** A stored value as state; anything unusable reads as the empty state. */
export function parseNotificationState(value: unknown): NotificationSeenState {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return EMPTY_NOTIFICATION_STATE;
  const saved = value as Record<string, unknown>;
  return { seenIds: sanitizeIds(saved['seenIds']), readIds: sanitizeIds(saved['readIds']) };
}

/** True when the state marks nothing — stored as no row at all. */
export function isEmptyNotificationState(state: NotificationSeenState): boolean {
  return !state.seenIds.length && !state.readIds.length;
}

/** Each id set capped to the newest MAX_IDS. */
export function capNotificationState(state: NotificationSeenState): NotificationSeenState {
  return { seenIds: state.seenIds.slice(0, MAX_IDS), readIds: state.readIds.slice(0, MAX_IDS) };
}

/** Prepends ids to a set, deduplicated, newest first. */
export function withIds(existing: string[], added: string[]): string[] {
  const merged = [...added, ...existing];
  return Array.from(new Set(merged));
}

function sanitizeIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string').slice(0, MAX_IDS);
}
